import React, { FunctionComponent, ReactElement, useState } from 'react';
import { useSelector } from 'react-redux';
import { Redirect, Route, Switch, useLocation } from 'react-router-dom';
import { AppStore } from '@akademiasaas/shared';
import { safeContinuePath } from '~/utils/safeContinuePath';
import ImpersonationModal from '~/components/ImpersonationModal/ImpersonationModal';
import Login from './components/Login/Login';
import { Register } from './components/Register/Register';
import { ForgotPassword } from './components/ForgotPassword/ForgotPassword';
import { ResetPassword } from './components/ResetPassword/ResetPassword';
import { SignWithLink } from './components/SignWithLink/SignWithLink';
import LoginByEmail from './components/LoginByEmail/LoginByEmail';
import AuthLayout from './layout/AuthLayout';

/**
 * Ekrany dla gości (logowanie, rejestracja, reset hasła z formularza): zalogowany użytkownik
 * z wczytanymi danymi trafia do aplikacji (`continue` lub `/`). Trasy z linków z e-maila
 * (reset-password, sign-with-link, login-by-link) bez guarda — mogą dotyczyć innego konta.
 */
const GuestScreen: FunctionComponent<{ children: ReactElement }> = ({ children }) => {
  const location = useLocation();
  const { data, details } = useSelector((store: AppStore) => store.user);

  if (data && details) {
    return <Redirect to={safeContinuePath(new URLSearchParams(location.search).get('continue'))} />;
  }

  return children;
};

/** Ekrany /auth w jednej ramce (wstęgi i nagłówek nie montują się od nowa przy zmianie ekranu). */
const Auth: FunctionComponent = () => {
  const location = useLocation();
  const [impersonateOpen, setImpersonateOpen] = useState(
    () => new URLSearchParams(location.search).get('impersonate') === 'true'
  );

  return (
    <>
      <AuthLayout>
        <Switch>
          <Route exact path="/auth/forgot-password/:email?">
            <GuestScreen>
              <ForgotPassword />
            </GuestScreen>
          </Route>
          <Route exact path="/auth/login">
            <GuestScreen>
              <Login />
            </GuestScreen>
          </Route>
          <Route exact path="/auth/register">
            <GuestScreen>
              <Register />
            </GuestScreen>
          </Route>
          <Route exact path="/auth/reset-password" component={ResetPassword} />
          <Route exact path="/auth/sign-with-link" component={SignWithLink} />
          <Route exact path="/auth/login-by-link" component={LoginByEmail} />
          <Redirect from="/" exact to="/auth/login" />
          <Redirect from="/auth" exact to="/auth/login" />
        </Switch>
      </AuthLayout>
      <ImpersonationModal open={impersonateOpen} onClose={() => setImpersonateOpen(false)} />
    </>
  );
};

export default Auth;
